import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Archive, Megaphone, MessagesSquare, Search, Users } from 'lucide-react'
import { useShellStore } from '@/stores/shellStore'
import { broadcastsQueryOptions } from '@/utils/broadcastQueryOptions'
import { groupChatsQueryOptions } from '@/utils/groupChatQueryOptions'
import { groupRostersQueryOptions } from '@/utils/rosterQueryOptions'
import { shortTime } from '@/lib/format'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

interface RailRowData {
  id: string
  name: string
  time: string
  tag: string
  kind: 'broadcast' | 'chat' | 'group'
  preview: string
  /** Broadcasts only: who sent it. Blank on any sent before this was recorded. */
  sender?: string
  /** Groups only: people on the roster with no mobile number on file. */
  unreachable?: number
}

export function Rail() {
  const tab = useShellStore((s) => s.tab)
  const setTab = useShellStore((s) => s.setTab)
  const listQuery = useShellStore((s) => s.listQuery)
  const setListQuery = useShellStore((s) => s.setListQuery)
  const selectedId = useShellStore((s) => s.selectedId)
  const view = useShellStore((s) => s.view)
  const selectConversation = useShellStore((s) => s.selectConversation)
  const selectGroup = useShellStore((s) => s.selectGroup)
  const openComposer = useShellStore((s) => s.openComposer)
  const openNewChat = useShellStore((s) => s.openNewChat)
  const unreachableOnly = useShellStore((s) => s.unreachableOnly)
  const setUnreachableOnly = useShellStore((s) => s.setUnreachableOnly)

  // Archived chats are kept forever as a record. They're a separate view rather
  // than mixed into the live list — nothing can be sent to them.
  const [showArchived, setShowArchived] = useState(false)

  const broadcasts = useQuery({ ...broadcastsQueryOptions(), enabled: tab === 'broadcasts' })
  const chats = useQuery({
    ...groupChatsQueryOptions(showArchived),
    enabled: tab === 'chats',
  })
  const groups = useQuery({ ...groupRostersQueryOptions(), enabled: tab === 'groups' })

  const q = listQuery.trim().toLowerCase()

  let rows: RailRowData[] = []
  if (tab === 'broadcasts') {
    rows = (broadcasts.data?.broadcasts ?? []).map((b) => ({
      id: b.id,
      name: b.name || 'Broadcast',
      time: shortTime(b.updatedAt),
      tag: 'Broadcast',
      kind: 'broadcast' as const,
      preview: b.preview || b.message,
      sender: b.senderName,
    }))
  } else if (tab === 'chats') {
    rows = (chats.data?.chats ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      time: shortTime(c.updatedAt),
      tag: showArchived ? 'Archived' : 'Group chat',
      kind: 'chat' as const,
      preview: `${c.participantCount} ${c.participantCount === 1 ? 'person' : 'people'}`,
    }))
  } else {
    rows = (groups.data ?? [])
      // A group nobody would notice is fine to hide behind the filter; a group
      // that would leave somebody out is the whole reason this tab exists.
      .filter((g) => !unreachableOnly || g.counts.unreachable > 0)
      .map((g) => ({
        id: g.id,
        name: g.displayName,
        time: '',
        // "Overridden" is worth calling out: what Entra shows for that group
        // is not who the text reaches.
        tag: g.hasOverlays ? 'Overridden' : 'Group',
        kind: 'group' as const,
        preview: g.error
          ? g.error
          : `${g.counts.textable} ${g.counts.textable === 1 ? 'person gets' : 'people get'} the text`,
        unreachable: g.counts.unreachable,
      }))
  }
  if (q) {
    rows = rows.filter(
      (r) => r.name.toLowerCase().includes(q) || r.sender?.toLowerCase().includes(q),
    )
  }

  const activeQuery = tab === 'broadcasts' ? broadcasts : tab === 'chats' ? chats : groups
  const isLoading = activeQuery.isPending
  const error = activeQuery.error

  const tabClass = (active: boolean) =>
    cn(
      'inline-flex flex-1 items-center justify-center gap-1 rounded-[9px] border py-2.5 text-[12.5px] font-bold transition-colors',
      active
        ? 'border-primary bg-primary text-on-primary'
        : 'border-border bg-surface text-muted-foreground hover:text-foreground',
    )

  return (
    <div className="flex h-full min-h-0 flex-col bg-rail">
      {/* Two distinct ways to start something: a solid indigo megaphone for the
          one-way broadcast, an outlined teal bubble for the shared chat. */}
      <div className="flex flex-col gap-2 p-3.5 pb-2.5">
        <button
          type="button"
          onClick={openComposer}
          className="flex w-full items-center justify-center gap-2 rounded-[11px] bg-primary py-3 text-sm font-bold text-on-primary shadow-wha-sm"
        >
          <Megaphone className="h-4 w-4" />
          New broadcast
        </button>
        <button
          type="button"
          onClick={openNewChat}
          className="flex w-full items-center justify-center gap-2 rounded-[11px] border-[1.5px] border-teal bg-teal-tint py-[11px] text-sm font-bold text-foreground hover:bg-teal-soft"
        >
          <MessagesSquare className="h-4 w-4 text-teal" />
          New group chat
        </button>
      </div>

      <div className="flex gap-1.5 px-3.5 pb-2.5">
        <button type="button" onClick={() => setTab('broadcasts')} className={tabClass(tab === 'broadcasts')}>
          <Megaphone className="h-[15px] w-[15px] flex-none" />
          Broadcasts
        </button>
        <button type="button" onClick={() => setTab('chats')} className={tabClass(tab === 'chats')}>
          <MessagesSquare className="h-[15px] w-[15px] flex-none" />
          Chats
        </button>
        <button type="button" onClick={() => setTab('groups')} className={tabClass(tab === 'groups')}>
          <Users className="h-[15px] w-[15px] flex-none" />
          Groups
        </button>
      </div>

      {tab === 'chats' && (
        <div className="px-3.5 pb-2.5">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-[8px] border px-2.5 py-1.5 text-[12px] font-bold transition-colors',
              showArchived
                ? 'border-primary bg-primary-tint text-primary'
                : 'border-border bg-surface text-muted-foreground hover:text-foreground',
            )}
          >
            <Archive className="h-[13px] w-[13px]" />
            {showArchived ? 'Showing archived' : 'Show archived'}
          </button>
        </div>
      )}

      {tab === 'groups' && (
        <div className="px-3.5 pb-2.5">
          <button
            type="button"
            onClick={() => setUnreachableOnly(!unreachableOnly)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-[8px] border px-2.5 py-1.5 text-[12px] font-bold transition-colors',
              unreachableOnly
                ? 'border-warn-border bg-warn-tint text-warn-ink'
                : 'border-border bg-surface text-muted-foreground hover:text-foreground',
            )}
          >
            <AlertTriangle className="h-[13px] w-[13px]" />
            {unreachableOnly ? 'Showing missing numbers' : 'Only missing numbers'}
          </button>
        </div>
      )}

      <div className="px-3.5 pb-3">
        <div className="flex items-center gap-2 rounded-[10px] border border-border bg-surface-2 px-3 py-2.5">
          <Search className="h-[15px] w-[15px] text-faint" />
          <input
            value={listQuery}
            onChange={(e) => setListQuery(e.target.value)}
            placeholder="Search…"
            className="w-full border-none bg-transparent text-[13.5px] text-foreground outline-none"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {isLoading ? (
          <div className="space-y-2 px-1.5">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-[11px]" />
            ))}
          </div>
        ) : error ? (
          <div className="px-3 py-6 text-center text-[13px] text-destructive">
            Couldn't load {tab}.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-8 text-center text-[13px] text-faint">
            {emptyMessage(tab, { showArchived, unreachableOnly })}
          </div>
        ) : (
          rows.map((row) => {
            const active = row.id === selectedId && view !== 'composer'
            return (
              <button
                key={row.id}
                type="button"
                onClick={() =>
                  row.kind === 'group' ? selectGroup(row.id) : selectConversation(row.id, row.kind)
                }
                style={{ borderLeftColor: active ? 'hsl(var(--primary))' : 'transparent' }}
                className={cn(
                  'mb-[3px] w-full rounded-[11px] border-l-[3px] px-3 py-[11px] text-left transition-colors',
                  active ? 'bg-primary-tint' : 'bg-transparent hover:bg-surface-2',
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-[13.5px] font-bold text-foreground">{row.name}</span>
                  {row.unreachable ? (
                    <span className="inline-flex flex-none items-center gap-1 rounded-[5px] bg-warn-tint px-1.5 py-0.5 text-[10px] font-extrabold text-warn-ink">
                      <AlertTriangle className="h-[10px] w-[10px]" />
                      {row.unreachable}
                    </span>
                  ) : (
                    <span className="flex-none text-[11px] text-faint">{row.time}</span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-1.5">
                  <span
                    className={cn(
                      'inline-flex flex-none items-center gap-1 rounded-[5px] px-1.5 py-0.5 text-[9.5px] font-extrabold uppercase tracking-[0.04em]',
                      row.kind === 'chat'
                        ? 'bg-teal-soft text-teal'
                        : row.kind === 'group'
                          ? 'bg-surface-2 text-muted-foreground'
                          : 'bg-primary-tint text-primary',
                    )}
                  >
                    {row.kind === 'chat' ? (
                      <MessagesSquare className="h-[11px] w-[11px]" />
                    ) : row.kind === 'group' ? (
                      <Users className="h-[11px] w-[11px]" />
                    ) : (
                      <Megaphone className="h-[11px] w-[11px]" />
                    )}
                    {row.tag}
                  </span>
                  <span className="truncate text-[12px] text-muted-foreground">{row.preview}</span>
                </div>
                {row.sender && (
                  <div className="mt-1 truncate text-[11.5px] font-semibold text-muted-foreground">
                    Sent by {row.sender}
                  </div>
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

function emptyMessage(
  tab: 'broadcasts' | 'chats' | 'groups',
  { showArchived, unreachableOnly }: { showArchived: boolean; unreachableOnly: boolean },
): string {
  if (tab === 'broadcasts') return 'No broadcasts yet. Hit "New broadcast" to send one.'
  if (tab === 'chats') {
    return showArchived
      ? 'No archived chats yet. Closed chats are kept here permanently.'
      : 'No group chats yet. Hit "New group chat" to start one.'
  }
  return unreachableOnly
    ? 'Every group can reach everyone on its roster — nobody is missing a mobile number.'
    : 'No SMS groups found in Microsoft 365.'
}
